import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorResponse } from "oidc-client-ts";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AuthStateProvider, type AuthState } from "../auth/AuthContext";
import { completeCallbackOnce } from "../auth/callback";
import { securityPrincipalKey } from "../auth/roles";
import { useLogin } from "../auth/useLogin";
import {
  authSignInMessage,
  authState,
  completeSignIn,
  initializeAuth,
  isAuthEnabled,
  refreshWorkspaceSession,
  subscribeAuth,
} from "../lib/auth";
import { createAppQueryClient } from "../queryClient";
import { Button } from "./ui";
import isaraMarkUrl from "./isara-mark.svg";
import "./auth.css";

type AuthView =
  | { kind: "checking" }
  | { kind: "callback" }
  | { kind: "authed"; auth: AuthState }
  | { kind: "signed-out"; message: string | null }
  | { kind: "session-error"; message: string }
  | { kind: "error"; message: string };

const WORKSPACE_SESSION_ATTEMPTS = 2;

class WorkspaceSessionLoadError extends Error {
  public constructor(cause: unknown) {
    super(readableAuthError(cause));
    this.name = "WorkspaceSessionLoadError";
  }
}

export default function AuthGate({
  callbackUrl,
  callbackError,
  children,
}: {
  callbackUrl: string | null;
  callbackError: string | null;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialLocation = useRef({
    pathname: location.pathname,
    search: location.search,
  });
  const initialCheckComplete = useRef(false);
  const [view, setView] = useState<AuthView>({ kind: "checking" });
  const [checkAttempt, setCheckAttempt] = useState(0);

  const publishState = useCallback(() => {
    // Restoring the OIDC user emits before effective workspace access is
    // loaded. Keep the domain app detached until both startup checks finish.
    if (!initialCheckComplete.current) return;
    const next = authState();
    setView(
      next.authenticated
        ? { kind: "authed", auth: next }
        : { kind: "signed-out", message: authSignInMessage() },
    );
  }, []);

  useEffect(() => subscribeAuth(publishState), [publishState]);

  useEffect(() => {
    // React Router may replace the navigate function after callback routing.
    // The startup flow is already authoritative at that point; do not restart
    // it from the captured callback location.
    if (initialCheckComplete.current) return;
    let cancelled = false;
    async function checkAuth() {
      try {
        await initializeAuth();
        if (cancelled) return;
        if (
          initialLocation.current.pathname === "/auth/callback" &&
          isAuthEnabled()
        ) {
          if (callbackError) throw new Error(callbackError);
          let returnTo = "/";
          if (callbackUrl) {
            setView({ kind: "callback" });
            returnTo = await completeCallbackOnce(callbackUrl, () =>
              completeSignIn(callbackUrl),
            );
          } else if (!authState().authenticated) {
            throw new Error("OAuth response is missing");
          }
          if (!cancelled) {
            await refreshWorkspaceSessionWithRetry();
            if (cancelled) return;
            // Leave the callback route before mounting the domain app so its
            // fallback route cannot race the OAuth return target.
            navigate(returnTo, { replace: true });
            initialCheckComplete.current = true;
            publishState();
          }
          return;
        }
        if (!cancelled) {
          await refreshWorkspaceSessionWithRetry();
          if (cancelled) return;
          initialCheckComplete.current = true;
          publishState();
        }
      } catch (error) {
        if (!cancelled) {
          if (
            error instanceof WorkspaceSessionLoadError &&
            authState().authenticated
          ) {
            setView({ kind: "session-error", message: error.message });
            return;
          }
          if (initialLocation.current.pathname === "/auth/callback") {
            navigate("/login", { replace: true });
          }
          setView({
            kind: "error",
            message: readableAuthError(error),
          });
        }
      }
    }
    void checkAuth();
    return () => {
      cancelled = true;
    };
  }, [callbackError, callbackUrl, checkAttempt, navigate, publishState]);

  const retryWorkspaceSession = useCallback(() => {
    initialCheckComplete.current = false;
    setView({ kind: "checking" });
    setCheckAttempt((attempt) => attempt + 1);
  }, []);

  const returnTo = useMemo(
    () => safeSignInReturnTo(location.pathname, location.search),
    [location.pathname, location.search],
  );

  if (view.kind === "authed") {
    const principalKey = securityPrincipalKey(
      view.auth.identity,
      view.auth.roles,
    );
    return (
      <PrincipalQueryBoundary key={principalKey}>
        <AuthStateProvider value={view.auth}>{children}</AuthStateProvider>
      </PrincipalQueryBoundary>
    );
  }
  if (view.kind === "signed-out") {
    return (
      <LoginScreen
        returnTo={returnTo}
        error={view.message ?? undefined}
        reauthenticate={view.message !== null}
      />
    );
  }
  if (view.kind === "session-error") {
    return (
      <WorkspaceSessionErrorScreen
        message={view.message}
        onRetry={retryWorkspaceSession}
      />
    );
  }
  if (view.kind === "error")
    return <LoginScreen returnTo={returnTo} error={view.message} />;
  return (
    <div className="login-screen">
      <div className="login-card" role="status" aria-live="polite">
        <h1>
          Isara <small>sign in</small>
        </h1>
        <p className="login-sub">
          {view.kind === "callback"
            ? "Completing sign in..."
            : "Loading sign in..."}
        </p>
      </div>
    </div>
  );
}

async function refreshWorkspaceSessionWithRetry(): Promise<void> {
  let lastError: unknown = new Error("Workspace session could not be loaded");
  for (let attempt = 0; attempt < WORKSPACE_SESSION_ATTEMPTS; attempt += 1) {
    try {
      await refreshWorkspaceSession();
      return;
    } catch (error: unknown) {
      lastError = error;
    }
  }
  throw new WorkspaceSessionLoadError(lastError);
}

function safeSignInReturnTo(pathname: string, search: string): string {
  if (pathname === "/auth/callback") return "/";
  if (pathname === "/login") return "/";
  return `${pathname}${search}`;
}

function readableAuthError(error: unknown): string {
  if (error instanceof ErrorResponse && error.error === "access_denied") {
    return (
      "This account is not invited to Isara Financial Modeling yet. " +
      "Ask your Isara engagement team to update your access."
    );
  }
  return error instanceof Error ? error.message : "Sign-in failed";
}

/**
 * Keep every effective principal in a physically separate QueryClient. Merely
 * clearing a shared cache is insufficient: an old mutation can settle after
 * logout and run its onSuccess callback. Its captured client is harmless once
 * this keyed boundary has detached it from the next session.
 */
function PrincipalQueryBoundary({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createAppQueryClient);

  useEffect(
    () => () => {
      void queryClient.cancelQueries();
      queryClient.clear();
    },
    [queryClient],
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}

function WorkspaceSessionErrorScreen({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="login-screen">
      <div className="login-card" role="alert">
        <h1>
          Isara <small>connection</small>
        </h1>
        <p className="login-sub">
          You are signed in, but the workspace could not finish loading.
        </p>
        <div className="banner err">{message}</div>
        <Button tone="primary" className="login-submit" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

function LoginScreen({
  returnTo,
  error,
  reauthenticate = false,
}: {
  returnTo: string;
  error?: string;
  reauthenticate?: boolean;
}) {
  const login = useLogin();
  const submit = () => login.mutate(returnTo);
  const mutationError =
    login.error instanceof Error ? login.error.message : null;

  return (
    <div className="login-screen">
      <div className="login-card">
        <h1>
          Isara <small>sign in</small>
        </h1>
        <p className="login-sub">
          Use the account your Isara engagement team invited.
        </p>
        {(error || mutationError) && (
          <div className="banner err" role="alert">
            {error || mutationError}
          </div>
        )}
        <Button
          tone="primary"
          className="login-submit"
          iconLeft={
            <img className="login-submit__mark" src={isaraMarkUrl} alt="" />
          }
          disabled={login.isPending}
          onClick={submit}
        >
          {login.isPending
            ? "Redirecting…"
            : reauthenticate
              ? "Sign in again"
              : "Sign in"}
        </Button>
      </div>
    </div>
  );
}
